import { clsx } from 'clsx'
import { addLockListener } from 'hook/useBodyScrollLock'
import { useRefState } from 'hook/useRefState'
import { observer } from 'mobx-react-lite'
import { OverlayScrollbars } from 'overlayscrollbars'
import { BasicHeader } from 'page/BasicHeader/BasicHeader'
import { Footer } from 'page/Footer/Footer'
import { Header } from 'page/Header/Header'
import { StandPage } from 'page/StandPage/StandPage'
import { ReactNode, useEffect, useState } from 'react'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { getPaddingStyle, Padding, styles } from 'util/style'
import css from './Page.module.scss'

type Props = {
  children: ReactNode
  clear?: boolean
  blank?: boolean
  full?: boolean
  tall?: boolean
  long?: boolean
  dark?: boolean
  center?: boolean
  onBack?(): void
  noBack?: boolean
  pad?: Padding
}

const pages: Partial<Record<string, number>> = {}

function getScreenKey(): string {
  const { screen, profile } = store
  const id = profile?.identity_id?.substring(0, 8)
  if (id && screen === 'profile') return screen + '.' + id
  return screen
}

export const Page = observer(
  (props: Props) => {
    const {
      children,
      clear,
      blank,
      full,
      tall,
      long,
      dark,
      center,
      onBack,
      noBack,
      pad,
    } = props

    if (!!opt.stand2) {
      return <StandPage {...props} />
    }

    const screen = getScreenKey()

    const className = clsx(
      css.page,
      full ? css.full : css.simple,
      tall && css.tall,
      long && css.long,
      clear && css.clear,
      blank && css.blank,
      dark && css.dark,
      center && css.center,
    )

    const [element, setElement] = useRefState<HTMLDivElement>()
    const [os, setOs] = useRefState<OverlayScrollbars>()
    const [lock, setLock] = useState(false)
    const padding = getPaddingStyle(pad)
    const style = styles({ ...padding })

    useEffect(() => {
      return addLockListener(event => setLock(event.lock))
    }, [])

    useEffect(() => {
      if (!element) return

      const os = OverlayScrollbars(
        { target: element },
        {
          showNativeOverlaidScrollbars: true,
          overflow: { y: 'hidden' },
          scrollbars: { autoHide: 'leave', autoHideDelay: 500 },
        },
      )
      setOs(os)

      return () => os.destroy()
    }, [element])

    useEffect(() => {
      if (os) os.options({
        overflow: { y: lock ? 'hidden' : 'scroll' },
      })
    }, [os, lock])

    useEffect(() => {
      if (!os) return
      const { viewport } = os.elements()

      const listener = () => {
        pages[screen] = viewport.scrollTop
      }
      viewport.addEventListener('scroll', listener)

      return () => viewport.removeEventListener('scroll', listener)
    }, [os])

    useEffect(() => {
      if (!os) return
      const { viewport } = os.elements()
      viewport.scrollTop = pages[screen] ?? 0
    }, [os, screen])

    return (
      <div className={className}>
        {opt.basic ? <BasicHeader /> : <Header noBack={noBack} onBack={onBack} />}

        {full && (
          <div className={css.appearance}>
            <div className={css.container} style={style}>
              {children}
            </div>
          </div>
        )}

        {!full && (
          <div className={css.appearance}>
            <div ref={setElement} className={css.scroll}>
              <div className={css.container} style={style}>
                {children}
              </div>
            </div>
          </div>
        )}

        {opt.footer && (
          <div className={css.footer}>
            <Footer />
          </div>
        )}
      </div>
    )
  },
)
